import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import api from '../../services/api';
import { toast } from 'react-toastify';

const BrokerWalletsTable = () => {
    const [wallets, setWallets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const formatCurrency = (value) => {
        const num = Number(value);
        if (isNaN(num)) return '0.00';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    };

    // --- Data Fetching ---
    const fetchWallets = useCallback(async (page, limit, search = '') => {
        setLoading(true);
        try {
            let queryParams = `page=${page}&per_page=${limit}`;
            if (search) {
                queryParams += `&search=${encodeURIComponent(search)}`;
            }

            const response = await api.get(`/wallet/admin/brokers?${queryParams}`);
            if (response.data?.status) {
                const items = response.data.response?.data || [];
                setWallets(items);
                setTotalRows(response.data.response?.total || 0);
            } else {
                setWallets([]);
                setTotalRows(0);
                toast.error('Failed to fetch broker wallets.');
            }
        } catch (error) {
            console.error('Error fetching broker wallets:', error);
            toast.error('Failed to fetch broker wallets.');
            setWallets([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch on mount and when pagination changes
    useEffect(() => {
        fetchWallets(currentPage, perPage, searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage]);

    // Debounced search - skip initial mount
    const isInitialMount = React.useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const debounceTimer = setTimeout(() => {
            setCurrentPage(1);
            fetchWallets(1, perPage, searchTerm);
        }, 400);
        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const columns = [
        {
            name: 'Name',
            selector: (row) => row.broker?.name || 'N/A',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Email',
            selector: (row) => row.broker?.email || 'N/A',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Mobile',
            selector: (row) => row.broker?.mobile || 'N/A',
            sortable: true,
        },

        {
            name: 'Total Balance',
            selector: (row) => Number(row.wallet?.totalBalance) || 0,
            cell: (row) => (
                <span className="fw-semibold">{formatCurrency(row.wallet?.totalBalance)}</span>
            ),
            sortable: true,
            right: true,
        },
        {
            name: 'Ready to Collect',
            selector: (row) => Number(row.wallet?.readyToCollect) || 0,
            cell: (row) => (
                <span className="text-success fw-semibold">{formatCurrency(row.wallet?.readyToCollect)}</span>
            ),
            sortable: true,
            right: true,
        },
        {
            name: 'Pending Balance',
            selector: (row) => Number(row.wallet?.pendingBalance) || 0,
            cell: (row) => (
                <span className="text-warning fw-semibold">{formatCurrency(row.wallet?.pendingBalance)}</span>
            ),
            sortable: true,
            right: true,
        },
        {
            name: 'Transactions',
            selector: (row) => row.transactions?.total || 0,
            sortable: true,
            center: true,
            width: '130px',
        },
    ];

    // CSV export - flatten the nested data
    const csvData = wallets.map((item) => ({
        name: item.broker?.name || '',
        email: item.broker?.email || '',
        mobile: item.broker?.mobile || '',
        status: item.broker?.status || '',
        totalBalance: item.wallet?.totalBalance || '0.00',
        readyToCollect: item.wallet?.readyToCollect || '0.00',
        pendingBalance: item.wallet?.pendingBalance || '0.00',
        totalTransactions: item.transactions?.total || 0,
    }));

    const csvHeaders = [
        { label: 'Name', key: 'name' },
        { label: 'Email', key: 'email' },
        { label: 'Mobile', key: 'mobile' },
        { label: 'Status', key: 'status' },
        { label: 'Total Balance', key: 'totalBalance' },
        { label: 'Ready to Collect', key: 'readyToCollect' },
        { label: 'Pending Balance', key: 'pendingBalance' },
        { label: 'Total Transactions', key: 'totalTransactions' },
    ];

    const customStyles = {
        rows: {
            style: {
                minHeight: '50px',
                fontSize: '15px',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#f8f9fa',
                fontWeight: '600',
                fontSize: '1rem',
            },
        },
        cells: {
            style: {
                fontSize: '0.95rem',
                padding: '12px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                {/* Search and Export Controls */}
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <input
                        type="text"
                        placeholder="Search by name, email or mobile..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control"
                        style={{ maxWidth: '350px' }}
                    />

                    <CSVLink
                        data={csvData}
                        headers={csvHeaders}
                        filename={`broker-wallets-page-${currentPage}.csv`}
                        className="btn btn-secondary"
                    >
                        Export CSV
                    </CSVLink>
                </div>

                {/* DataTable */}
                <DataTable
                    columns={columns}
                    data={wallets}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    highlightOnHover
                    responsive
                    striped
                    progressPending={loading}
                    customStyles={customStyles}
                    noDataComponent="No broker wallets found"
                    paginationComponentOptions={{
                        rowsPerPageText: 'Rows per page:',
                        rangeSeparatorText: 'of',
                    }}
                />
            </div>
        </div>
    );
};

export default BrokerWalletsTable;
